import { useState } from 'react';
import { ChevronDown, ChevronUp, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import type { CmsSectionDef } from '../content/registry';
import type { CmsListItem } from '../content/fallbacks';
import { CMS_MAX_FIELD_LENGTH, CMS_MAX_ITEM_ARRAY_ENTRIES, CMS_MAX_ITEM_ARRAY_ENTRY_LENGTH, CMS_MAX_LIST_ITEMS } from '../content/limits';
import type { AdminCmsSection, CmsListSectionValue, CmsSectionShape, CmsSectionValue } from '../api/cms';

/**
 * Registry-driven marketing-CMS field editor (§ full-CMS-coverage build). Used
 * from both the platform-admin editor (`pages/admin/Cms.tsx`) and the reseller
 * editor (`pages/reseller/Content.tsx`) — the two differ only in WHICH sections
 * they show (the registry's `resellerEditable` flag) and at what scope they
 * save, not in how a section is edited.
 *
 * A section is one of two shapes (see `content/registry.ts` for the field
 * metadata and `content/fallbacks.ts` for the built-in copy):
 *   - 'single': a flat map of string fields, each a labelled textarea.
 *   - 'list': `{ items: [...] }` — a repeatable card per item, with add / remove /
 *     move-up / move-down controls. A field within an item can itself be a
 *     string[] (`list: true`) — edited as one textarea, either one entry per
 *     line (short bullets) or split on blank lines (paragraphs).
 *
 * No live DB row yet → the editor pre-fills the section with its FALLBACK copy
 * (so the admin/reseller can see and tweak the real live text, not a blank
 * form) and shows a "Using default copy" badge; once saved, a "Customized"
 * badge appears alongside a "Reset to default" button that deletes the row
 * (DELETE /admin|reseller/cms/:key) and reverts the fields back to the fallback.
 */

const TEXTAREA_CLASS =
  'w-full px-3 py-2 rounded-md text-sm border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 ' +
  'text-zinc-950 dark:text-zinc-50 focus:ring-2 focus:ring-brand/40 focus:border-brand resize-y ' +
  'placeholder:text-zinc-400 dark:placeholder:text-zinc-500';

function Badge({ customized }: { customized: boolean }) {
  return customized ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
      Customized
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
      Using default copy
    </span>
  );
}

function SectionHeader({ spec, customized }: { spec: CmsSectionDef; customized: boolean }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
      <div className="flex items-center gap-2.5">
        <h3 className="font-semibold text-zinc-950 dark:text-zinc-50">{spec.label}</h3>
        <Badge customized={customized} />
      </div>
      <span className="text-xs text-zinc-400 dark:text-zinc-500 mono">{spec.key}</span>
    </div>
  );
}

/** Inline error banner for a save/reset failure — mirrors CashPaymentModal's error text style. */
function SectionError({ message }: { message: string }) {
  return (
    <div className="mt-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
      {message}
    </div>
  );
}

export interface CmsSectionEditorProps {
  spec: CmsSectionDef;
  existing: AdminCmsSection | undefined;
  /** SINGLE sections: the built-in copy (also used as each field's placeholder). */
  fallbackSingle: CmsSectionValue;
  /** LIST sections: the built-in items. */
  fallbackList: CmsListItem[];
  /** Persist the section (admin: saveCmsSection; reseller: saveResellerCmsSection). */
  save: (sectionKey: string, value: CmsSectionShape) => Promise<unknown>;
  /** Delete the row / revert to default (admin: clearCmsSection; reseller: clearResellerCmsSection). */
  clear: (sectionKey: string) => Promise<unknown>;
  /** Called after a successful save or reset — invalidate whatever queries the caller cares about. */
  onSaved: () => Promise<unknown> | void;
}

export function CmsSectionEditor(props: CmsSectionEditorProps) {
  return props.spec.kind === 'list' ? <ListSectionEditor {...props} /> : <SingleSectionEditor {...props} />;
}

function isListValue(value: unknown): value is CmsListSectionValue {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'items' in (value as Record<string, unknown>) &&
    Array.isArray((value as CmsListSectionValue).items)
  );
}

function SingleSectionEditor({ spec, existing, fallbackSingle, save, clear, onSaved }: CmsSectionEditorProps) {
  const customized = existing !== undefined && !isListValue(existing.value);
  const existingValue = customized ? (existing!.value as CmsSectionValue) : undefined;
  // No row yet → every field starts BLANK (true HTML-placeholder semantics: the
  // fallback text is shown only as a grayed hint, never pre-filled as real
  // content) — this also keeps the couple of "empty = keep the specially-styled
  // built-in headline" fields (product.hero / landing.hero) safe by construction.
  const baseline: CmsSectionValue = Object.fromEntries(spec.fields.map((f) => [f.name, existingValue?.[f.name] ?? '']));
  const [draft, setDraft] = useState<CmsSectionValue>(baseline);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);

  async function onSave() {
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await save(spec.key, draft);
      await onSaved();
      setSaved(true);
    } catch {
      setError('Could not save this section. Check the fields and try again.');
    } finally {
      setBusy(false);
    }
  }

  async function onReset() {
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await clear(spec.key);
      await onSaved();
      setDraft(Object.fromEntries(spec.fields.map((f) => [f.name, ''])));
    } catch {
      setError('Could not reset this section. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5 sm:p-6">
      <SectionHeader spec={spec} customized={customized} />
      {spec.note && <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">{spec.note}</p>}
      <div className="mt-4 space-y-3">
        {spec.fields.map((field) => (
          <label key={field.name} className="block">
            <span className="block text-xs font-medium text-zinc-600 dark:text-zinc-300 mb-1">{field.label}</span>
            <textarea
              rows={field.multiline ? 3 : 1}
              value={draft[field.name] ?? ''}
              placeholder={fallbackSingle[field.name]}
              onChange={(e) => setDraft((d) => ({ ...d, [field.name]: e.target.value }))}
              maxLength={CMS_MAX_FIELD_LENGTH}
              className={TEXTAREA_CLASS}
            />
          </label>
        ))}
      </div>
      {error && <SectionError message={error} />}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Button onClick={onSave} disabled={busy || !dirty} iconLeft={<Save className="w-4 h-4" />}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        {customized && (
          <Button variant="secondary" size="sm" onClick={onReset} disabled={busy} iconLeft={<RotateCcw className="w-4 h-4" />}>
            Reset to default
          </Button>
        )}
        {saved && !dirty && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
      </div>
    </Card>
  );
}

/** Join/split an `list: true` item field per its delimiter (default: one entry per line). */
function joinListField(values: string[], delimiter: 'line' | 'blank' | undefined): string {
  return values.join(delimiter === 'blank' ? '\n\n' : '\n');
}
function splitListField(text: string, delimiter: 'line' | 'blank' | undefined): string[] {
  return delimiter === 'blank'
    ? text.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p !== '')
    : text.split('\n');
}

/** Drop blank trailing/only-whitespace entries from a `list: true` field before saving (line-delimited only — 'blank' already trims/filters on split). */
function sanitizeItems(items: CmsListItem[], fields: CmsSectionDef['fields']): CmsListItem[] {
  return items.map((item) => {
    const out: CmsListItem = { ...item };
    for (const f of fields) {
      if (f.list && Array.isArray(out[f.name]) && (f.listDelimiter ?? 'line') === 'line') {
        out[f.name] = (out[f.name] as string[]).filter((line) => line.trim() !== '');
      }
    }
    return out;
  });
}

function ListSectionEditor({ spec, existing, fallbackList, save, clear, onSaved }: CmsSectionEditorProps) {
  // Row-existence, matching SingleSectionEditor — NOT items.length > 0. The API
  // now deletes a LIST row outright when it's saved with 0 items (reset
  // semantics, see cms.service.ts `set()`), so a 0-length row can't persist; this
  // still drops the length check so the badge/Reset button never again disagree
  // with what the live page renders for whatever row shape does exist.
  const customized = existing !== undefined && isListValue(existing.value);
  const baseline: CmsListItem[] = customized ? (existing!.value as CmsListSectionValue).items : fallbackList;
  const emptyItem = (): CmsListItem => Object.fromEntries(spec.fields.map((f) => [f.name, f.list ? [] : '']));
  const [items, setItems] = useState<CmsListItem[]>(baseline);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(items) !== JSON.stringify(baseline);
  // Clamped to the server's actual cap (CMS_MAX_LIST_ITEMS) regardless of what the
  // registry declares, so a future registry typo can't silently produce a save
  // that always 400s.
  const maxItems = Math.min(spec.maxItems ?? CMS_MAX_LIST_ITEMS, CMS_MAX_LIST_ITEMS);
  const noun = spec.itemLabel ?? 'item';

  function updateField(idx: number, name: string, value: string | string[]) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [name]: value } : it)));
  }
  function addItem() {
    setItems((prev) => (prev.length >= maxItems ? prev : [...prev, emptyItem()]));
  }
  function removeItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }
  function moveItem(idx: number, dir: -1 | 1) {
    setItems((prev) => {
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target] as CmsListItem, next[idx] as CmsListItem];
      return next;
    });
  }

  async function onSave() {
    // Defense in depth: the Add button already disables at maxItems, so this
    // should be unreachable in normal use — but block Save with a clear message
    // rather than let an over-cap list round-trip to a 400 the user never sees.
    if (items.length > CMS_MAX_LIST_ITEMS) {
      setError(`This section can have at most ${CMS_MAX_LIST_ITEMS} ${noun}s — remove some before saving.`);
      return;
    }
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await save(spec.key, { items: sanitizeItems(items, spec.fields) });
      await onSaved();
      setSaved(true);
    } catch {
      setError('Could not save this section. Check the fields and try again.');
    } finally {
      setBusy(false);
    }
  }

  async function onReset() {
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await clear(spec.key);
      await onSaved();
      setItems(fallbackList);
    } catch {
      setError('Could not reset this section. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5 sm:p-6">
      <SectionHeader spec={spec} customized={customized} />
      {spec.note && <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">{spec.note}</p>}
      <div className="mt-4 space-y-4">
        {items.map((item, idx) => (
          <div key={idx} className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {noun} {idx + 1}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0}
                  aria-label={`Move ${noun} ${idx + 1} up`}
                  className="w-7 h-7 grid place-items-center rounded-md text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === items.length - 1}
                  aria-label={`Move ${noun} ${idx + 1} down`}
                  className="w-7 h-7 grid place-items-center rounded-md text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  aria-label={`Remove ${noun} ${idx + 1}`}
                  className="w-7 h-7 grid place-items-center rounded-md text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-3 space-y-2.5">
              {spec.fields.map((field) => {
                const raw = item[field.name];
                const value = field.list ? joinListField(Array.isArray(raw) ? raw : [], field.listDelimiter) : ((raw as string) ?? '');
                return (
                  <label key={field.name} className="block">
                    <span className="block text-xs font-medium text-zinc-600 dark:text-zinc-300 mb-1">
                      {field.label}
                      {field.list && (
                        <span className="ml-1 font-normal normal-case text-zinc-400 dark:text-zinc-500">
                          {field.listDelimiter === 'blank' ? '(one per paragraph, blank line between)' : '(one per line)'}
                        </span>
                      )}
                    </span>
                    <textarea
                      rows={field.list ? (field.listDelimiter === 'blank' ? 8 : 4) : field.multiline ? 3 : 1}
                      value={value}
                      onChange={(e) => updateField(idx, field.name, field.list ? splitListField(e.target.value, field.listDelimiter) : e.target.value)}
                      // A `list: true` field's textarea holds ALL of its entries joined together
                      // (see joinListField above), so this is a rough combined ceiling (entries ×
                      // per-entry cap, plus separator slack) — not a substitute for the server's
                      // per-entry validation, just enough to block an obviously-oversized paste
                      // before it round-trips to a 400. Plain fields use the exact per-field cap.
                      maxLength={field.list ? CMS_MAX_ITEM_ARRAY_ENTRIES * (CMS_MAX_ITEM_ARRAY_ENTRY_LENGTH + 2) : CMS_MAX_FIELD_LENGTH}
                      className={TEXTAREA_CLASS}
                    />
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-zinc-500 dark:text-zinc-400">No {noun}s yet — add one below.</p>}
      </div>
      {error && <SectionError message={error} />}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Button variant="secondary" size="sm" onClick={addItem} disabled={items.length >= maxItems} iconLeft={<Plus className="w-4 h-4" />}>
          Add {noun}
        </Button>
        <Button onClick={onSave} disabled={busy || !dirty} iconLeft={<Save className="w-4 h-4" />}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        {customized && (
          <Button variant="secondary" size="sm" onClick={onReset} disabled={busy} iconLeft={<RotateCcw className="w-4 h-4" />}>
            Reset to default
          </Button>
        )}
        {saved && !dirty && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
      </div>
    </Card>
  );
}
